import * as React from 'react';
import { Link } from 'react-router-dom';

import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons/faCircleCheck';
import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons/faCircleExclamation';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons/faCircleNotch';
import { faCopy } from '@fortawesome/free-solid-svg-icons/faCopy';
import { faEdit } from '@fortawesome/free-solid-svg-icons/faEdit';
import { faEllipsisVertical } from '@fortawesome/free-solid-svg-icons/faEllipsisVertical';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons/faTriangleExclamation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ActionIcon, Anchor, Flex, Menu, Paper, Popover, SimpleGrid, Stack, Text, Tooltip } from '@mantine/core';
import { useClipboard, useDisclosure } from '@mantine/hooks';

import { stellarApiEnhanced } from '../api/enhancedApiCalls';
import { AppRoutes, makeExperimentRoute } from '../appRoutes';
import { DeleteExperimentModal } from './DeleteExperimentModal';
import { EditExperimentModal } from './EditExperimentModal';
import { formatAntibodyValidationMessages } from './experimentStatusMessageFormatter';
import { InfoTooltip } from '../common/InfoTooltip';
import { DATE_FORMAT_OPTIONS } from '../constants/date-constants';
import { EXPERIMENT_CARD_HEIGHT, EXPERIMENT_CARD_WIDTH } from '../constants/layout-constants';
import type { AntibodyValidationMetadata, ExperimentResponseWithSummary } from '../generated/generatedOpenAPI';
import { useAppDispatch } from '../store/hooks';
import { getClonotypeDefinitionLabel } from '../store/slices/preprocessing.slice';
import { useGetAnalysisTaskStatusForExperiment, useGetPendingProcessingStatus, useGetSummaryData } from '../store/store';
import { formatDate } from '../utils';
import { formatFailureStatus, formatProgressStatus } from '../utils/processingSteps';

const s = {
  progressIconWrapper: 'relative inline-block',
  validationWarningBadge: 'absolute -top-[6px] -right-[8px] text-[0.65em] leading-none',
} as const;

function ExperimentCardOptionsMenu({ experiment }: { experiment: ExperimentResponseWithSummary }) {
  const [editOpened, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const [cloneOpened, { open: openClone, close: closeClone }] = useDisclosure(false);
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);
  return (
    <>
      <Menu shadow="md" position="bottom-end" width={100}>
        <Menu.Target>
          <ActionIcon data-testid="experiment-settings" variant="subtle" mr={-7} color="dark">
            <FontAwesomeIcon icon={faEllipsisVertical} />
          </ActionIcon>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Item
            data-testid="edit-experiment-button"
            onClick={openEdit}
            leftSection={<FontAwesomeIcon icon={faEdit} size="sm" style={{ marginTop: '-2px' }} />}
          >
            Edit
          </Menu.Item>
          {/* <Menu.Item
            data-testid="clone-experiment-button"
            onClick={openClone}
            leftSection={<FontAwesomeIcon icon={faClone} size="sm" style={{ marginTop: '-2px' }} />}
            disabled
          >
            Clone
          </Menu.Item> */}
          <Menu.Item
            data-testid="delete-experiment-button"
            onClick={openDelete}
            leftSection={<FontAwesomeIcon icon={faTrash} size="sm" style={{ marginTop: '-2px' }} />}
            color="red"
          >
            Delete
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      <EditExperimentModal key={editOpened ? 'editOpened' : 'editClosed'} experiment={experiment} opened={editOpened} close={closeEdit} />
      {/* <CloneExperimentModal key={cloneOpened ? 'cloneOpened' : 'cloneClosed'} experiment={experiment} opened={cloneOpened} close={closeClone} /> */}
      <DeleteExperimentModal key={deleteOpened ? 'deleteOpened' : 'deleteClosed'} experiment={experiment} opened={deleteOpened} close={closeDelete} />
    </>
  );
}

function InfoPopoverRow({ label, value, dataTestId }: { label: string; value: string; dataTestId?: string }) {
  return (
    <SimpleGrid cols={2} spacing="xs" pb="xs">
      <Text c="dimmed" size="sm">
        {label}:
      </Text>
      <Tooltip label={value} withArrow openDelay={500}>
        <Text size="sm" lineClamp={1} data-testid={dataTestId}>
          {value}
        </Text>
      </Tooltip>
    </SimpleGrid>
  );
}

function InfoPopover({ disabled = false, experiment }: { disabled?: boolean; experiment: ExperimentResponseWithSummary }) {
  const [opened, setOpened] = React.useState(false);
  const clipboard = useClipboard({ timeout: 2000 });
  const info = React.useMemo(() => {
    const fileNameAntibody = experiment?.antibodyFile?.split('/').pop() || '-';
    const clonotypingLabel = getClonotypeDefinitionLabel(experiment.clonotypingMethods?.[0] || '-');

    return [
      { dataTestId: 'clonotyping-method', label: 'Clonotyping Method', value: `${clonotypingLabel}` },
      {
        dataTestId: 'source-file',
        label: 'Source file',
        value: fileNameAntibody,
      },
    ];
  }, [experiment]);

  return (
    <Popover width="target" offset={-1} position="bottom" shadow="xl" onChange={(o: boolean) => setOpened(o)} closeOnClickOutside>
      {!disabled && (
        <Popover.Target>
          <ActionIcon data-testid="experiment-card-expand-button" w="100%" variant="subtle" color="dvGray" disabled={disabled} py="md">
            <Flex gap="md" justify="center" align="center">
              <FontAwesomeIcon
                icon={faChevronDown}
                style={{
                  transition: 'transform 0.1s ease',
                  transform: opened ? 'rotate(-180deg)' : 'rotate(0deg)',
                }}
                size="xs"
              />
            </Flex>
          </ActionIcon>
        </Popover.Target>
      )}
      <Popover.Dropdown data-testid={`experiment-card-details-${experiment.title}`}>
        {info.map((item) => (
          <InfoPopoverRow dataTestId={item.dataTestId} key={item.label} label={item.label} value={item.value} />
        ))}

        <Flex align="center" gap="xs" data-testid="experiment-card-id">
          <Text c="gray.5" size="xs" fw="italic" opacity={0.6}>
            <i>ID: {experiment.id.slice(0, 5)}</i>
          </Text>
          <Tooltip label={clipboard.copied ? 'Copied!' : 'Copy full ID'} withArrow>
            <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => clipboard.copy(experiment.id)} data-testid="copy-experiment-id-button">
              <FontAwesomeIcon icon={faCopy} size="xs" />
            </ActionIcon>
          </Tooltip>
        </Flex>
      </Popover.Dropdown>
    </Popover>
  );
}

export function ExperimentCard({
  experiment,
  validationResult,
}: {
  experiment: ExperimentResponseWithSummary;
  validationResult?: AntibodyValidationMetadata | null;
}) {
  // todo: add formatting and display of validation issues in the card

  const dispatch = useAppDispatch();

  const isAnalysisPending = (taskState: string | undefined) => {
    if (!taskState) {
      return true;
    }
    const unfinishedStates = ['PENDING', 'STARTED', 'RETRY', 'RECEIVED'];
    return unfinishedStates.includes(taskState);
  };

  const [shouldPoll, setShouldPoll] = React.useState<boolean>(isAnalysisPending(experiment.analysisState as string | undefined));

  const { data: taskStatus } = useGetAnalysisTaskStatusForExperiment(
    {
      experimentId: experiment.id as string,
    },
    {
      pollingInterval: shouldPoll ? 1000 : undefined,
      skip: !shouldPoll,
    },
  );

  const { data: pendingProcessingStatus } = useGetPendingProcessingStatus(
    {
      experimentId: experiment.id as string,
    },
    {
      pollingInterval: shouldPoll ? 1000 : undefined,
      skip: !shouldPoll,
    },
  );

  // Shares a cache entry with the polling query above, so without refetchOnMountOrArgChange it
  // would reuse whichever mid-run step the last poll left behind instead of fetching the failure.
  const isFailed = (taskStatus || experiment.analysisState) === 'FAILURE';
  const { data: failedStepStatus } = useGetPendingProcessingStatus(
    { experimentId: experiment.id as string },
    { skip: !isFailed, refetchOnMountOrArgChange: true },
  );

  const { data: summaryData } = useGetSummaryData(
    { id: experiment.id as string },
    {
      skip: !experiment.id || experiment.analysisState !== 'SUCCESS',
    },
  );

  React.useEffect(() => {
    if (taskStatus) {
      const isPending = isAnalysisPending(taskStatus);
      setShouldPoll(isPending);

      if (!isPending) {
        // We need to manually invalidate the experiment cache to ensure experiment attributes are updated
        dispatch(stellarApiEnhanced.util.invalidateTags([{ type: 'EXPERIMENT', id: 'LIST' }]));
      }
    }
  }, [dispatch, experiment.id, taskStatus]);

  const progressStatus = React.useMemo(() => {
    const state = taskStatus || experiment.analysisState;

    switch (state) {
      case 'SUCCESS':
        return 'Analysis complete';
      case 'FAILURE':
        return formatFailureStatus(failedStepStatus);
      case 'PENDING':
        return 'Waiting for processing to start...';
      case 'STARTED': {
        if (!pendingProcessingStatus) {
          return 'Processing started...';
        }
        const detailedStatus = formatProgressStatus(pendingProcessingStatus);
        return detailedStatus || 'Processing in progress...';
      }
      default:
        return undefined;
    }
  }, [experiment.analysisState, taskStatus, pendingProcessingStatus, failedStepStatus]);

  const analysisState = taskStatus || experiment.analysisState;
  const canVisitExperiment = analysisState === 'SUCCESS';

  const showProgressIndicator = React.useMemo(() => {
    return progressStatus !== undefined;
  }, [progressStatus]);

  const progressIconProps = React.useMemo(() => {
    if (!showProgressIndicator) {
      return undefined;
    }
    if (analysisState === 'FAILURE') {
      return {
        icon: faCircleExclamation,
        color: 'var(--mantine-color-red-6)',
        spin: false,
        dataTestId: 'experiment-card-progress-error',
      } as const;
    }
    if (analysisState === 'SUCCESS') {
      return {
        icon: faCircleCheck,
        color: 'var(--mantine-color-green-6)',
        spin: false,
        dataTestId: 'experiment-card-progress-success',
      } as const;
    }
    return {
      icon: faCircleNotch,
      color: 'var(--mantine-color-blue-6)',
      spin: true,
      dataTestId: 'experiment-card-progress-running',
    } as const;
  }, [analysisState, showProgressIndicator]);

  const summaryCellsAfterFiltering = React.useMemo(() => {
    return summaryData?.cellsAfterFiltering ?? experiment.summary?.cellsAfterFiltering ?? null;
  }, [experiment.summary?.cellsAfterFiltering, summaryData?.cellsAfterFiltering]);

  const summaryCellsWithPairedVhVl = React.useMemo(() => {
    return summaryData?.cellsWithProdVhVl ?? summaryData?.cellsWithPairedVhVl ?? experiment.summary?.cellsWithPairedVhVl ?? null;
  }, [experiment.summary?.cellsWithPairedVhVl, summaryData?.cellsWithPairedVhVl, summaryData?.cellsWithProdVhVl]);

  // When the experiment completed successfully but the source file had validation warnings,
  // surface a badge so users know to interpret results with caution.
  const validationWarningMessage = React.useMemo(() => {
    if (!validationResult || analysisState !== 'SUCCESS') {
      return null;
    }
    const { detailed } = formatAntibodyValidationMessages(validationResult);
    const warnings = detailed.filter((m) => m.severity === 'warning');
    if (warnings.length === 0) {
      return null;
    }
    return warnings.map((m) => m.message).join(' ');
  }, [validationResult, analysisState]);

  return (
    <Paper shadow="md" withBorder h={EXPERIMENT_CARD_HEIGHT} w={EXPERIMENT_CARD_WIDTH} style={{ position: 'relative' }} data-testid="experiment-card">
      <Stack h="100%" gap={0}>
        <Stack gap="sm" flex={1} p="lg" pb={0} bg="white">
          <Flex justify="space-between" align="center">
            <Flex align="center" gap="sm" style={{ flex: 1, minWidth: 0 }}>
              <InfoTooltip
                label={experiment.title}
                position="top"
                styles={{ arrow: { borderTop: 'none', borderLeft: 'none', borderRight: '2px solid #0064B0', borderBottom: '2px solid #0064B0' } }}
                disabled={experiment.title.length <= 30}
              >
                <Anchor
                  style={{
                    flex: 1,
                    minWidth: 0,
                    ...(!canVisitExperiment
                      ? {
                          pointerEvents: 'none',
                          cursor: 'not-allowed',
                        }
                      : {}),
                  }}
                  component={Link}
                  to={makeExperimentRoute(experiment.id as string, AppRoutes.ExperimentDataOverview)}
                  size="lg"
                  fw="500"
                  c="black"
                  lineClamp={1}
                  data-testid="experiment-card-title"
                >
                  {experiment.title}
                </Anchor>
              </InfoTooltip>
              {showProgressIndicator && progressIconProps && (
                <InfoTooltip
                  label={validationWarningMessage ?? progressStatus ?? ''}
                  position="right"
                  styles={{ tooltip: { maxWidth: 300, whiteSpace: 'normal', wordBreak: 'break-word' } }}
                >
                  <span data-testid={progressIconProps.dataTestId} className={s.progressIconWrapper}>
                    <FontAwesomeIcon icon={progressIconProps.icon} color={progressIconProps.color} spin={progressIconProps.spin} />
                    {validationWarningMessage && (
                      <span data-testid="experiment-card-progress-warning-badge" className={s.validationWarningBadge}>
                        <FontAwesomeIcon icon={faTriangleExclamation} color="var(--mantine-color-yellow-6)" />
                      </span>
                    )}
                  </span>
                </InfoTooltip>
              )}
            </Flex>
            <ExperimentCardOptionsMenu experiment={experiment} />
          </Flex>
          <Stack gap="xs">
            <SimpleGrid cols={2} spacing="xs">
              <Text size="sm">{formatDate(experiment.creationDate, DATE_FORMAT_OPTIONS.FULL_DATE)}</Text>
              <Text size="sm" ta="right">
                {formatDate(experiment.creationDate, DATE_FORMAT_OPTIONS.TIME)}
              </Text>
            </SimpleGrid>

            <SimpleGrid cols={2} spacing="xs">
              <Text c="dimmed" size="sm">
                Organism:
              </Text>
              <Text size="sm" data-testid="experiment-card-organism">
                {experiment.organism.charAt(0).toUpperCase() + experiment.organism.slice(1)}
              </Text>
            </SimpleGrid>

            <SimpleGrid cols={2} spacing="xs">
              <Text c="dimmed" size="sm">
                Number of cells:
              </Text>
              <Text size="sm" data-testid="total-filtered-cells">
                {summaryCellsAfterFiltering ?? '-'}
              </Text>
            </SimpleGrid>

            <SimpleGrid cols={2} spacing="xs">
              <Text c="dimmed" size="sm">
                Paired chains:
              </Text>
              <Text size="sm" data-testid="cells-with-paired-vh-vl">
                {summaryCellsWithPairedVhVl ?? '-'}
              </Text>
            </SimpleGrid>
          </Stack>
        </Stack>
        <InfoPopover experiment={experiment} />
      </Stack>
    </Paper>
  );
}
